import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, FileText, ReceiptText, MapPin, Calendar,
  CheckCircle2, XCircle, Clock, Loader2, CreditCard, Coins,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { sharePdfBlob } from "@/lib/downloadPdf";

/**
 * ClientHistory — "Mes courses" screen (route: /dashboard/client/history).
 *
 * Lists the client's past + upcoming bookings and exposes 2 PDF actions
 * per row:
 *   • Bon de commande — pré-prestation, includes the detailed price
 *     breakdown (PEC + distance × price/km + surge + credit + total).
 *   • Facture — issued once the ride is paid + completed (driver is the
 *     legal issuer, Saor is the intermediary).
 *
 * The download uses `URL.createObjectURL(blob)` so mobile Safari + Chrome
 * both open the PDF in the WebView instead of forcing a hostile download
 * prompt — better UX on Capacitor iOS/Android.
 */

const STATUS_META = {
  pending:     { label: "En attente",  Icon: Clock,        tone: "bg-amber-50 text-amber-700 border-amber-200" },
  accepted:    { label: "Acceptée",    Icon: Loader2,      tone: "bg-blue-50 text-blue-700 border-blue-200" },
  picked_up:   { label: "Chauffeur arrivé", Icon: Loader2, tone: "bg-blue-50 text-blue-700 border-blue-200" },
  in_progress: { label: "En cours",    Icon: Loader2,      tone: "bg-blue-50 text-blue-700 border-blue-200" },
  completed:   { label: "Terminée",    Icon: CheckCircle2, tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled:   { label: "Annulée",     Icon: XCircle,      tone: "bg-rose-50 text-rose-700 border-rose-200" },
};

function fmtEuro(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toFixed(2).replace(".", ",") + " €";
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) +
      " · " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch (_) { return iso; }
}

export default function ClientHistory() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyPdfId, setBusyPdfId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/client/bookings");
      setBookings(data?.bookings || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Impossible de charger vos courses");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (bookingId, kind) => {
    // Un seul bouton par document : on ouvre directement la feuille de
    // partage native (iOS/Android). Depuis là, le client peut : voir le
    // PDF, l'enregistrer dans Fichiers, l'envoyer par iMessage/Mail/
    // WhatsApp… C'est plus fiable et plus riche qu'un simple téléchargement.
    const key = `${bookingId}-${kind}`;
    setBusyPdfId(key);
    try {
      const path = kind === "invoice" ? "invoice.pdf" : "receipt.pdf";
      const res = await api.get(`/client/bookings/${bookingId}/${path}`, { responseType: "blob" });
      const filename = `saor-${kind === "invoice" ? "facture" : "bon-commande"}-${bookingId.slice(0, 8)}.pdf`;
      await sharePdfBlob(res.data, filename, {
        title: kind === "invoice" ? "Facture Saor" : "Bon de commande Saor",
        text: kind === "invoice"
          ? "Voici ma facture Saor."
          : "Voici mon bon de commande Saor.",
      });
    } catch (err) {
      const msg = String(err?.message || "");
      if (!msg.toLowerCase().includes("abort") && !msg.toLowerCase().includes("cancel")) {
        toast.error(formatApiError(err) || "Impossible de générer le PDF");
      }
    } finally {
      setBusyPdfId(null);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-secondary/30 to-white pb-24"
      style={{ paddingTop: "max(24px, env(safe-area-inset-top))" }}
      data-testid="client-history-page"
    >
      <header className="px-5 pb-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-3 py-1.5 text-sm font-medium text-foreground shadow-sm backdrop-blur transition hover:bg-white"
          data-testid="history-back-btn"
        >
          <ChevronLeft className="h-4 w-4" /> Retour
        </button>
        <div className="mt-4">
          <p className="text-[10px] font-medium uppercase tracking-[0.24em] text-primary/70">Saor · Historique</p>
          <h1 className="mt-1 font-[Outfit] text-3xl font-semibold tracking-tight text-foreground">Mes courses</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Consultez vos bons de commande et factures à tout moment.
          </p>
        </div>
      </header>

      <section className="mx-5 space-y-3">
        {loading ? (
          <div className="grid place-items-center py-16 text-sm text-muted-foreground">Chargement…</div>
        ) : bookings.length === 0 ? (
          <div className="grid place-items-center rounded-3xl border border-border/60 bg-white py-14 text-center">
            <ReceiptText className="mb-3 h-10 w-10 text-primary/60" />
            <p className="font-[Outfit] text-lg font-semibold text-foreground">Aucune course pour le moment</p>
            <p className="mt-1 max-w-xs text-sm text-muted-foreground">
              Vos réservations apparaîtront ici avec leur bon de commande et leur facture.
            </p>
          </div>
        ) : (
          bookings.map((b) => {
            const meta = STATUS_META[b.status] || STATUS_META.pending;
            const isPaid = b.payment_status === "paid";
            const isBusyReceipt = busyPdfId === `${b.id}-receipt`;
            const isBusyInvoice = busyPdfId === `${b.id}-invoice`;
            return (
              <article
                key={b.id}
                className="overflow-hidden rounded-2xl border border-border/60 bg-white shadow-[0_10px_30px_-15px_rgba(15,38,52,0.15)]"
                data-testid={`history-booking-${b.id.slice(0, 8)}`}
              >
                {/* Status strip */}
                <div className={`flex items-center justify-between border-b px-4 py-2 ${meta.tone}`}>
                  <span className="inline-flex items-center gap-1.5 font-[Outfit] text-[11px] font-semibold uppercase tracking-widest">
                    <meta.Icon className="h-3.5 w-3.5" />
                    {meta.label}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-widest opacity-70">
                    <Calendar className="h-3 w-3" />
                    {fmtDate(b.created_at)}
                  </span>
                </div>

                {/* Route */}
                <div className="grid gap-2 px-4 py-3">
                  <div className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-4 w-4 flex-none text-primary" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] font-medium text-foreground">{b.departure_address || "—"}</p>
                      <p className="truncate text-[11px] text-muted-foreground">→ {b.arrival_address || "—"}</p>
                    </div>
                  </div>
                </div>

                {/* Price + payment */}
                <div className="flex items-center justify-between border-t border-neutral-100 bg-neutral-50/50 px-4 py-2.5">
                  <div className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    {b.payment_method === "card" ? <CreditCard className="h-3.5 w-3.5" /> : <Coins className="h-3.5 w-3.5" />}
                    <span>
                      {b.payment_method === "card" ? "Carte / Apple Pay" : "Paiement à bord"}
                      {isPaid && " · payé"}
                    </span>
                  </div>
                  <span className="font-[Outfit] text-base font-semibold tabular-nums text-foreground">
                    {fmtEuro(b.price)}
                  </span>
                </div>

                {/* PDF actions — un seul bouton par document. Le clic
                    ouvre directement la feuille de partage native (iOS/
                    Android) : visualiser, enregistrer dans Fichiers,
                    envoyer par iMessage/Mail/WhatsApp, etc. */}
                <div className="grid grid-cols-2 gap-2 p-3">
                  <button
                    type="button"
                    onClick={() => downloadPdf(b.id, "receipt")}
                    disabled={isBusyReceipt}
                    className="flex items-center justify-center gap-1.5 rounded-xl border border-primary/25 bg-white px-3 py-2 text-[12.5px] font-medium text-primary transition hover:bg-primary/5 disabled:opacity-60"
                    data-testid={`history-receipt-${b.id.slice(0, 8)}`}
                  >
                    {isBusyReceipt
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <FileText className="h-3.5 w-3.5" />}
                    {isBusyReceipt ? "…" : "Bon de commande"}
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadPdf(b.id, "invoice")}
                    disabled={!isPaid || isBusyInvoice}
                    className="flex items-center justify-center gap-1.5 rounded-xl border border-primary/25 bg-white px-3 py-2 text-[12.5px] font-medium text-primary transition hover:bg-primary/5 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`history-invoice-${b.id.slice(0, 8)}`}
                    title={isPaid ? "Facture" : "Facture disponible après paiement"}
                  >
                    {isBusyInvoice
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <ReceiptText className="h-3.5 w-3.5" />}
                    {isBusyInvoice ? "…" : "Facture"}
                  </button>
                </div>
              </article>
            );
          })
        )}
      </section>
    </div>
  );
}
